import type { Step } from "react-joyride";

export type TourRole = "ADMIN" | "BOARD_MEMBER" | "RESIDENT";

interface TourCopy {
  title: string;
  body: string;
}

interface RawStep {
  target: string;
  es: TourCopy;
  en: TourCopy;
  placement?: Step["placement"];
  disableBeacon?: boolean;
}

function build(lang: string, steps: RawStep[]): Step[] {
  const isEn = lang.startsWith("en");
  return steps.map((s) => {
    const copy = isEn ? s.en : s.es;
    return {
      target: s.target,
      placement: s.placement ?? "right",
      disableBeacon: s.disableBeacon ?? true,
      title: copy.title,
      content: copy.body,
    } as Step;
  });
}

const adminSteps: RawStep[] = [
  {
    target: '[data-tour="dashboard-welcome"]',
    placement: "bottom",
    es: {
      title: "¡Bienvenido al Portal!",
      body: "Este es el tablero principal: aquí ve un resumen del estado del condominio. Le guiaré por las funciones principales como administrador.",
    },
    en: {
      title: "Welcome to the Portal!",
      body: "This is the main dashboard. You'll see a snapshot of the community here. I'll walk you through the main admin features.",
    },
  },
  {
    target: '[data-tour="nav-users"]',
    es: {
      title: "Usuarios",
      body: "Gestione roles, unidades asignadas y estado activo de todos los usuarios del sistema.",
    },
    en: {
      title: "Users",
      body: "Manage roles, assigned units, and active status of every user in the system.",
    },
  },
  {
    target: '[data-tour="nav-units"]',
    es: {
      title: "Unidades",
      body: "Catálogo de apartamentos: titular, contacto, edificio, piso y ocupación.",
    },
    en: {
      title: "Units",
      body: "Catalog of apartments: owner, contact info, building, floor, and occupancy.",
    },
  },
  {
    target: '[data-tour="nav-staff"]',
    es: {
      title: "Personal",
      body: "Administre el equipo del condominio: puestos, fechas de inicio y estado activo.",
    },
    en: {
      title: "Staff",
      body: "Manage the condominium team: positions, start dates, and active status.",
    },
  },
  {
    target: '[data-tour="nav-meetings"]',
    es: {
      title: "Reuniones",
      body: "Programa juntas, asambleas y reuniones extraordinarias, con agenda, minutas y votos.",
    },
    en: {
      title: "Meetings",
      body: "Schedule board meetings and assemblies with agenda, minutes, and votes.",
    },
  },
  {
    target: '[data-tour="nav-resolutions"]',
    es: {
      title: "Resoluciones",
      body: "Revise resoluciones de la Junta con conteo de votos y trazabilidad completa.",
    },
    en: {
      title: "Resolutions",
      body: "Review board resolutions with vote tallies and full traceability.",
    },
  },
  {
    target: '[data-tour="nav-documents"]',
    es: {
      title: "Documentos",
      body: "Suba y versione el Reglamento, Manual de Normas y otros documentos oficiales.",
    },
    en: {
      title: "Documents",
      body: "Upload and version the Bylaws, Rules Manual, and other official documents.",
    },
  },
  {
    target: '[data-tour="nav-contracts"]',
    es: {
      title: "Contratos",
      body: "Contratos con proveedores, con alertas automáticas antes de cada vencimiento.",
    },
    en: {
      title: "Contracts",
      body: "Vendor contracts, with automatic alerts before each one expires.",
    },
  },
  {
    target: '[data-tour="nav-legal"]',
    es: {
      title: "Acciones Legales",
      body: "Dé seguimiento a asuntos legales en un tablero de Abierto a Cerrado.",
    },
    en: {
      title: "Legal Actions",
      body: "Track legal matters on a board from Open to Closed.",
    },
  },
  {
    target: '[data-tour="nav-operations-queue"]',
    es: {
      title: "Cola de Operaciones",
      body: "Vea de un vistazo todos los procesos pendientes del condominio — permisos, mantenimiento, gastos, resoluciones y más — con indicadores de si el trabajo avanza o se está estancando.",
    },
    en: {
      title: "Operations Queue",
      body: "See every pending process across the community at a glance — permits, maintenance, expenses, resolutions, and more — with indicators showing whether work is moving or stalling.",
    },
  },
  {
    target: '[data-tour="nav-finance"]',
    es: {
      title: "Finanzas",
      body: "Cuotas de mantenimiento, gastos, presupuestos y reportes financieros del condominio.",
    },
    en: {
      title: "Finance",
      body: "Maintenance fees, expenses, budgets, and financial reports for the community.",
    },
  },
  {
    target: '[data-tour="nav-fees"]',
    es: {
      title: "Cuotas de Mantenimiento",
      body: "Genere cuotas del mes, registre pagos y envíe recordatorios a las unidades.",
    },
    en: {
      title: "Maintenance Fees",
      body: "Generate monthly fees, record payments, and send reminders to units.",
    },
  },
  {
    target: '[data-tour="nav-expenses"]',
    es: {
      title: "Gastos",
      body: "Apruebe gastos del condominio: de pendiente a aprobado y pagado.",
    },
    en: {
      title: "Expenses",
      body: "Approve condominium expenses: from pending to approved and paid.",
    },
  },
  {
    target: '[data-tour="nav-budgets"]',
    es: {
      title: "Presupuestos",
      body: "Cree y apruebe el presupuesto anual por categoría.",
    },
    en: {
      title: "Budgets",
      body: "Create and approve the annual budget by category.",
    },
  },
  {
    target: '[data-tour="nav-reports"]',
    es: {
      title: "Informes Financieros",
      body: "Repositorio de informes mensuales y anuales para la Junta y los residentes.",
    },
    en: {
      title: "Financial Reports",
      body: "Repository of monthly and annual reports for the board and residents.",
    },
  },
  {
    target: '[data-tour="nav-monthly-close"]',
    es: {
      title: "Cierre Mensual",
      body: "Publique el resumen financiero del mes; los residentes lo ven solo una vez publicado.",
    },
    en: {
      title: "Monthly Close",
      body: "Publish the month's financial summary; residents see it only once published.",
    },
  },
  {
    target: '[data-tour="nav-works-permit"]',
    es: {
      title: "Solicitudes de Titulares",
      body: "Revise y apruebe solicitudes de obra sometidas por los residentes.",
    },
    en: {
      title: "Works Permits",
      body: "Review and approve work requests submitted by residents.",
    },
  },
  {
    target: '[data-tour="nav-maintenance"]',
    es: {
      title: "Mantenimiento",
      body: "Vea y asigne reportes de mantenimiento reportados por los residentes.",
    },
    en: {
      title: "Maintenance",
      body: "View and assign maintenance reports submitted by residents.",
    },
  },
  {
    target: '[data-tour="nav-announcements"]',
    es: {
      title: "Comunicados",
      body: "Publique avisos a los residentes. Aparecerán también en la página pública.",
    },
    en: {
      title: "Announcements",
      body: "Publish notices for residents. They'll also show on the public site.",
    },
  },
  {
    target: '[data-tour="nav-incidents"]',
    es: {
      title: "Incidentes",
      body: "Registre y dé seguimiento a incidentes: ruido, daños, violaciones de reglas y más.",
    },
    en: {
      title: "Incidents",
      body: "Log and track incidents: noise, damage, rule violations, and more.",
    },
  },
  {
    target: '[data-tour="nav-keys"]',
    es: {
      title: "Registro de Llaves",
      body: "Inventario de llaves del edificio con historial de entrega y devolución.",
    },
    en: {
      title: "Key Registry",
      body: "Building key inventory with check-out and check-in history.",
    },
  },
  {
    target: '[data-tour="nav-cameras"]',
    es: {
      title: "Acceso a Cámaras",
      body: "Controle qué usuarios pueden ver las cámaras de vigilancia.",
    },
    en: {
      title: "Camera Access",
      body: "Control which users can view the surveillance cameras.",
    },
  },
  {
    target: '[data-tour="nav-audit"]',
    es: {
      title: "Auditoría",
      body: "Registro inmutable de toda acción importante del sistema, para transparencia y cumplimiento.",
    },
    en: {
      title: "Audit log",
      body: "Immutable log of every important action in the system, for transparency and compliance.",
    },
  },
  {
    target: '[data-tour="nav-help"]',
    es: {
      title: "Centro de ayuda",
      body: "Puede repetir este tour o ver guías detalladas en cualquier momento desde aquí.",
    },
    en: {
      title: "Help center",
      body: "You can replay this tour or read detailed guides anytime from here.",
    },
  },
];

const boardSteps: RawStep[] = [
  {
    target: '[data-tour="dashboard-welcome"]',
    placement: "bottom",
    es: {
      title: "¡Bienvenido, miembro de Junta!",
      body: "Como miembro de la Junta, tiene acceso a las herramientas de gobernanza y finanzas. Le muestro las principales.",
    },
    en: {
      title: "Welcome, board member!",
      body: "As a board member you have access to governance and finance tools. Here are the main ones.",
    },
  },
  {
    target: '[data-tour="nav-meetings"]',
    es: {
      title: "Reuniones",
      body: "Aquí ve y participa en juntas y asambleas: agenda, minutas a aprobar y votación.",
    },
    en: {
      title: "Meetings",
      body: "View and participate in board meetings and assemblies: agenda, minutes, and voting.",
    },
  },
  {
    target: '[data-tour="nav-resolutions"]',
    es: {
      title: "Resoluciones",
      body: "Formalice decisiones de la Junta con votos registrados y trazabilidad completa.",
    },
    en: {
      title: "Resolutions",
      body: "Formalize board decisions with recorded votes and full traceability.",
    },
  },
  {
    target: '[data-tour="nav-documents"]',
    es: {
      title: "Documentos",
      body: "Repositorio compartido de reglamentos, actas y documentos oficiales.",
    },
    en: {
      title: "Documents",
      body: "Shared repository of bylaws, minutes, and official documents.",
    },
  },
  {
    target: '[data-tour="nav-contracts"]',
    es: {
      title: "Contratos",
      body: "Contratos con proveedores, con alertas automáticas antes de cada vencimiento.",
    },
    en: {
      title: "Contracts",
      body: "Vendor contracts, with automatic alerts before each one expires.",
    },
  },
  {
    target: '[data-tour="nav-legal"]',
    es: {
      title: "Acciones Legales",
      body: "Dé seguimiento a asuntos legales en un tablero de Abierto a Cerrado.",
    },
    en: {
      title: "Legal Actions",
      body: "Track legal matters on a board from Open to Closed.",
    },
  },
  {
    target: '[data-tour="nav-operations-queue"]',
    es: {
      title: "Cola de Operaciones",
      body: "Vea de un vistazo todos los procesos pendientes del condominio — permisos, mantenimiento, gastos, resoluciones y más — con indicadores de si el trabajo avanza o se está estancando.",
    },
    en: {
      title: "Operations Queue",
      body: "See every pending process across the community at a glance — permits, maintenance, expenses, resolutions, and more — with indicators showing whether work is moving or stalling.",
    },
  },
  {
    target: '[data-tour="nav-finance"]',
    es: {
      title: "Finanzas",
      body: "Revise cuotas, gastos, presupuestos y reportes financieros del condominio.",
    },
    en: {
      title: "Finance",
      body: "Review fees, expenses, budgets, and financial reports.",
    },
  },
  {
    target: '[data-tour="nav-fees"]',
    es: {
      title: "Cuotas de Mantenimiento",
      body: "Genere cuotas del mes, registre pagos y envíe recordatorios a las unidades.",
    },
    en: {
      title: "Maintenance Fees",
      body: "Generate monthly fees, record payments, and send reminders to units.",
    },
  },
  {
    target: '[data-tour="nav-expenses"]',
    es: {
      title: "Gastos",
      body: "Apruebe gastos del condominio: de pendiente a aprobado y pagado.",
    },
    en: {
      title: "Expenses",
      body: "Approve condominium expenses: from pending to approved and paid.",
    },
  },
  {
    target: '[data-tour="nav-budgets"]',
    es: {
      title: "Presupuestos",
      body: "Cree y apruebe el presupuesto anual por categoría.",
    },
    en: {
      title: "Budgets",
      body: "Create and approve the annual budget by category.",
    },
  },
  {
    target: '[data-tour="nav-reports"]',
    es: {
      title: "Informes Financieros",
      body: "Repositorio de informes mensuales y anuales para la Junta y los residentes.",
    },
    en: {
      title: "Financial Reports",
      body: "Repository of monthly and annual reports for the board and residents.",
    },
  },
  {
    target: '[data-tour="nav-monthly-close"]',
    es: {
      title: "Cierre Mensual",
      body: "Publique el resumen financiero del mes; los residentes lo ven solo una vez publicado.",
    },
    en: {
      title: "Monthly Close",
      body: "Publish the month's financial summary; residents see it only once published.",
    },
  },
  {
    target: '[data-tour="nav-works-permit"]',
    es: {
      title: "Solicitudes de Titulares",
      body: "Revise solicitudes de obra sometidas por los residentes.",
    },
    en: {
      title: "Works Permits",
      body: "Review work requests submitted by residents.",
    },
  },
  {
    target: '[data-tour="nav-maintenance"]',
    es: {
      title: "Mantenimiento",
      body: "Vea reportes de mantenimiento reportados por los residentes.",
    },
    en: {
      title: "Maintenance",
      body: "View maintenance reports submitted by residents.",
    },
  },
  {
    target: '[data-tour="nav-announcements"]',
    es: {
      title: "Comunicados",
      body: "Publique avisos para los residentes con un solo clic.",
    },
    en: {
      title: "Announcements",
      body: "Publish notices for residents in one click.",
    },
  },
  {
    target: '[data-tour="nav-incidents"]',
    es: {
      title: "Incidentes",
      body: "Vea y dé seguimiento a incidentes: ruido, daños, violaciones de reglas y más.",
    },
    en: {
      title: "Incidents",
      body: "View and track incidents: noise, damage, rule violations, and more.",
    },
  },
  {
    target: '[data-tour="nav-help"]',
    es: {
      title: "Centro de ayuda",
      body: "Puede repetir este tour o ver guías detalladas en cualquier momento desde aquí.",
    },
    en: {
      title: "Help center",
      body: "You can replay this tour or read detailed guides anytime from here.",
    },
  },
];

const residentSteps: RawStep[] = [
  {
    target: '[data-tour="dashboard-welcome"]',
    placement: "bottom",
    es: {
      title: "¡Bienvenido, vecino!",
      body: "Este es su portal personal de Costa Dorada I. Le muestro qué puede hacer aquí.",
    },
    en: {
      title: "Welcome, neighbor!",
      body: "This is your personal Costa Dorada I portal. Here's what you can do.",
    },
  },
  {
    target: '[data-tour="nav-resident-finances"]',
    es: {
      title: "Mis Finanzas",
      body: "Vea su saldo, cuotas pendientes y el historial de pagos de su unidad.",
    },
    en: {
      title: "My Finances",
      body: "See your balance, pending fees, and your unit's payment history.",
    },
  },
  {
    target: '[data-tour="nav-documents"]',
    es: {
      title: "Documentos",
      body: "Consulte el Reglamento y otros documentos oficiales del condominio.",
    },
    en: {
      title: "Documents",
      body: "Browse the Bylaws and other official condominium documents.",
    },
  },
  {
    target: '[data-tour="nav-maintenance"]',
    es: {
      title: "Mantenimiento",
      body: "Reporte un problema en su unidad o en áreas comunes. Le notificamos cuando se resuelva.",
    },
    en: {
      title: "Maintenance",
      body: "Report an issue in your unit or in common areas. We'll notify you when it's resolved.",
    },
  },
  {
    target: '[data-tour="nav-works-permit"]',
    es: {
      title: "Solicitudes de Titulares",
      body: "Solicite permisos para mejoras, remodelaciones o trabajos en su unidad.",
    },
    en: {
      title: "Works permits",
      body: "Request permits for improvements, remodels, or work in your unit.",
    },
  },
  {
    target: '[data-tour="nav-announcements"]',
    es: {
      title: "Comunicados",
      body: "Vea los avisos más recientes de la administración y la Junta.",
    },
    en: {
      title: "Announcements",
      body: "See the latest notices from administration and the board.",
    },
  },
  {
    target: '[data-tour="nav-help"]',
    es: {
      title: "Centro de ayuda",
      body: "Puede repetir este tour o consultar guías cuando lo necesite.",
    },
    en: {
      title: "Help center",
      body: "Replay this tour or browse guides whenever you need.",
    },
  },
];

export function getTour(role: TourRole, lang: string): Step[] {
  if (role === "ADMIN") return build(lang, adminSteps);
  if (role === "BOARD_MEMBER") return build(lang, boardSteps);
  return build(lang, residentSteps);
}
